import { prefersReducedMotion } from '@theme/utilities';

/**
 * Testimonials carousel: the track scrolls natively with CSS scroll-snap.
 * This element only builds the pagination dots, keeps the current dot in sync
 * with the scroll position and runs the optional autoplay.
 */
class TestimonialsCarousel extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #autoplayTimer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | null} */
  #scrollFrame = null;

  #paused = false;

  connectedCallback() {
    const { track } = this;
    if (!track) return;

    track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#resizeObserver = new ResizeObserver(() => this.#buildDots());
    this.#resizeObserver.observe(track);

    this.#buildDots();
    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.#resizeObserver?.disconnect();
    this.#stopAutoplay();
    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
  }

  /** @returns {HTMLElement | null} */
  get track() {
    return this.querySelector('[data-track]');
  }

  /** @returns {HTMLElement | null} */
  get dotsContainer() {
    return this.querySelector('[data-dots]');
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track?.children ?? []));
  }

  /**
   * Number of pages: one per scroll position where a slide can start.
   * @returns {number}
   */
  get pageCount() {
    const { track } = this;
    const slides = this.slides;
    const first = slides[0];
    if (!track || !first) return 0;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) return 1;

    const step = this.#slideStep();
    return Math.min(slides.length, Math.round(maxScroll / step) + 1);
  }

  /** Distance between the start of two consecutive slides. */
  #slideStep() {
    const [first, second] = this.slides;
    if (!first) return 1;
    if (!second) return first.offsetWidth || 1;
    return Math.abs(second.offsetLeft - first.offsetLeft) || first.offsetWidth || 1;
  }

  #currentPage() {
    const { track } = this;
    if (!track) return 0;
    const maxScroll = track.scrollWidth - track.clientWidth;
    // Snap the last dot when the track reaches its end (partial last page)
    if (maxScroll > 0 && Math.abs(track.scrollLeft) >= maxScroll - 2) return this.pageCount - 1;
    return Math.round(Math.abs(track.scrollLeft) / this.#slideStep());
  }

  #buildDots() {
    const container = this.dotsContainer;
    if (!container) return;

    const count = this.pageCount;
    if (count <= 1) {
      container.hidden = true;
      container.replaceChildren();
      return;
    }

    if (container.children.length !== count) {
      const dots = Array.from({ length: count }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'testimonials__dot';
        dot.setAttribute('aria-label', `${index + 1} / ${count}`);
        dot.addEventListener('click', () => this.goTo(index));
        return dot;
      });
      container.replaceChildren(...dots);
    }

    container.hidden = false;
    this.#updateDots();
  }

  #updateDots() {
    const container = this.dotsContainer;
    if (!container) return;
    const current = this.#currentPage();
    Array.from(container.children).forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === current));
    });
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    const { track } = this;
    const slide = this.slides[index];
    if (!track || !slide) return;

    const isRtl = getComputedStyle(track).direction === 'rtl';
    const offset = slide.offsetLeft - (this.slides[0]?.offsetLeft ?? 0);
    track.scrollTo({ left: isRtl ? -offset : offset });
  }

  #onScroll = () => {
    if (this.#scrollFrame !== null) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateDots();
    });
  };

  #startAutoplay() {
    const delay = Number(this.dataset.autoplay);
    if (!delay || prefersReducedMotion() || window.Shopify?.designMode) return;

    this.#stopAutoplay();
    this.#autoplayTimer = setInterval(() => {
      if (this.#paused || document.hidden) return;
      const count = this.pageCount;
      if (count <= 1) return;
      this.goTo((this.#currentPage() + 1) % count);
    }, delay);
  }

  #stopAutoplay() {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
